import React from 'react';

import { Box, Tooltip, Typography } from '@mui/material';

import { FraudScoreDetails } from './FraudScoreDetails';
import { SingleOrderPayments } from './SingleOrderPayments';

interface Props {
  order: App.Order;
}

function SingleFlightDetails({ order }: Props) {
  const formattedTotal = order.total.toLocaleString(undefined, { maximumFractionDigits: 0 });

  const firstFlightItem = order.flight_items[0];
  const journey = firstFlightItem?.journey;
  const departingFlights = journey?.departing?.flights || [];
  const returningFlights = journey?.returning?.flights || [];

  const firstDepartingFlight = departingFlights[0];
  const lastDepartingFlight = departingFlights[departingFlights.length - 1];

  // Calculate lead days from order creation to first departing flight
  let leadDays = 999;
  if (firstDepartingFlight?.departing_date) {
    leadDays = Math.floor(
      (new Date(firstDepartingFlight.departing_date).setHours(0, 0, 0, 0) -
        new Date(order.created_at).setHours(0, 0, 0, 0)) /
        (1000 * 60 * 60 * 24),
    );
  }

  // Build route info showing origin → final destination with stops count
  const formatRouteInfo = (flights: typeof departingFlights) => {
    if (!flights.length) return null;
    const origin = flights[0]?.departing_airport || 'Unknown';
    const destination = flights[flights.length - 1]?.arrival_airport || 'Unknown';
    const stops = flights.length - 1;
    const stopsLabel = stops > 0 ? ` (${stops} stop${stops > 1 ? 's' : ''})` : '';
    return `${origin} → ${destination}${stopsLabel}`;
  };

  const departingInfo = formatRouteInfo(departingFlights) || 'No departure info';
  const returningInfo = formatRouteInfo(returningFlights);

  // For country info, use origin of first segment and destination of last segment
  const departingCountry = firstDepartingFlight?.departing_country;
  const arrivalCountry = lastDepartingFlight?.arrival_country;

  const isAustralianDomestic = departingCountry === 'Australia' && arrivalCountry === 'Australia';

  return (
    <Box>
      <a target="_blank" href={`/purchases/${order.id}`} rel="noreferrer">
        flight
      </a>{' '}
      for {order.currency_code}
      {formattedTotal}{' '}
      {order.fk_purchaser_id && order.fk_purchaser_id !== order.fk_customer_id && <strong>Spoofed Order</strong>}
      <br />
      <strong>Account:</strong> {order.customer_full_name} | {order.customer_email} | {order.customer_phone_prefix}
      {order.customer_phone}
      <br />
      <strong>Route:</strong> {departingInfo}
      {returningInfo && <> | Return: {returningInfo}</>}
      <br />
      {leadDays}d lead time{' '}
      {leadDays <= 10 && (
        <Tooltip title="Fraudsters book quickly before we can catch them! Booking under 10 days in advance is a risk. Under 3 days is high risk.">
          <span>{leadDays <= 3 ? '🔴' : '🟠'}</span>
        </Tooltip>
      )}
      {leadDays > 10 && (
        <Tooltip title="Longer lead times are more likely to be genuine customers.">
          <span>🟢</span>
        </Tooltip>
      )}
      <br />
      <Typography component="span" sx={{ fontStyle: 'italic' }}>
        {departingCountry && arrivalCountry ? `${departingCountry} → ${arrivalCountry}` : 'Route countries unknown'}
      </Typography>{' '}
      {isAustralianDomestic && (
        <Tooltip title="Australian domestic flights are lower risk for fraud.">
          <span>🟢</span>
        </Tooltip>
      )}
      <br />
      <strong>Provider:</strong> {firstFlightItem?.provider || 'Unknown'}
      <br />
      <strong>Platform:</strong> {order.platform}
      <br />
      <FraudScoreDetails order={order} />
      <SingleOrderPayments order={order} />
    </Box>
  );
}

export { SingleFlightDetails };
